/**
 * Persist the in-flight Song Processing durable BulkOp across route changes
 * and refreshes so progress can resume when the operator returns.
 */

export type ActiveBulkOpVerb =
  | "Archiving"
  | "Restoring"
  | "Deleting"
  | "Reprocessing";

export type ActiveBulkOpSession = {
  bulkOpId: string;
  verb: ActiveBulkOpVerb;
  /** When true, show a compact chip instead of the blocking modal. */
  minimized: boolean;
  startedAt: string;
};

const STORAGE_KEY = "kk.sa.activeProcessingBulkOp.v1";
const CHANGE_EVENT = "kk-sa-active-bulk-op-change";

function canUseStorage(): boolean {
  return typeof window !== "undefined" && !!window.sessionStorage;
}

export function readActiveBulkOp(): ActiveBulkOpSession | null {
  if (!canUseStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveBulkOpSession;
    if (!parsed?.bulkOpId || !parsed?.verb) return null;
    return {
      bulkOpId: String(parsed.bulkOpId),
      verb: parsed.verb,
      minimized: !!parsed.minimized,
      startedAt: parsed.startedAt || new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

function writeActiveBulkOp(next: ActiveBulkOpSession | null): void {
  if (!canUseStorage()) return;
  try {
    if (!next) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // best-effort
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function startActiveBulkOp(
  bulkOpId: string,
  verb: ActiveBulkOpVerb,
): ActiveBulkOpSession {
  const session: ActiveBulkOpSession = {
    bulkOpId,
    verb,
    minimized: false,
    startedAt: new Date().toISOString(),
  };
  writeActiveBulkOp(session);
  return session;
}

export function minimizeActiveBulkOp(): void {
  const cur = readActiveBulkOp();
  if (!cur) return;
  writeActiveBulkOp({ ...cur, minimized: true });
}

export function expandActiveBulkOp(): void {
  const cur = readActiveBulkOp();
  if (!cur) return;
  writeActiveBulkOp({ ...cur, minimized: false });
}

export function clearActiveBulkOp(): void {
  writeActiveBulkOp(null);
}

export function subscribeActiveBulkOp(
  listener: () => void,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) listener();
  };
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}
